import React, { useState, useMemo } from "react";

import { reduce } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Input from "shared/components/Input";
import NextPreviousFooter from "shared/components/NextPreviousFooter";
import SelectGroup from "shared/components/SelectGroup";
import Space from "shared/components/Space";
import Spin from "shared/components/Spin";
import { StepStore } from "shared/components/Tab";
import { NAME_SPACES } from "shared/locals/constants";

import FoldersTitle from "../Common/FoldersTitle";

import "./styles.scss";

const ProductDetails = ({
  query: { data, loading },
  setActiveStep,
  step: { onBack },
  product,
  onCreate,
  warranty,
  loading: loadingCreate,
}) => {
  const { t } = useTranslation(NAME_SPACES.NEW_FOLDER, {
    keyPrefix: "BREAKDOWN",
  });
  const rules = useMemo(
    () =>
      (data?.rules?.data?.faultTypes || []).reduce((prevItem, currentItem) => {
        return {
          ...prevItem,
          [currentItem.typeDesc]: currentItem.faultGroupInfoList.reduce(
            (prevFaultGroup, currentFaultGroup) => {
              return {
                ...prevFaultGroup,
                [currentFaultGroup.groupDesc]:
                  currentFaultGroup.faultCodeInfoList.map((faultItem) => ({
                    value: faultItem.faultCode,
                    label: faultItem.faultDesc,
                  })),
              };
            },
            {},
          ),
        };
      }, {}),
    [data],
  );
  const rulesGroup = useMemo(
    () =>
      (data?.rules?.data?.faultTypes || []).reduce(
        (result, { typeCode, typeDesc, isMandatory }) => {
          return { ...result, [typeDesc]: { typeCode, isMandatory, typeDesc } };
        },
        {},
      ),
    [data],
  );
  const [faults, setFaults] = useState({});
  const [customerDefectsObs, setCustomerDefectsObs] = useState("");

  return (
    <>
      <div className={"newfolder--wrapper--content"}>
        <FoldersTitle title={t("TITLE")} subTitle={product.intervention.desc} />
        <div className={"newfolder--wrapper--content--search"}>
          {loading && <Spin />}
          {!loading && (
            <div className={"newfolder--wrapper--content--search--result"}>
              <div className="warranty">
                {loading ? (
                  <Spin />
                ) : (
                  <>
                    <p>{warranty}</p>
                  </>
                )}
              </div>
              {Object.keys(rules).map((key) => (
                <>
                  <SelectGroup
                    showSearch
                    mode="multiple"
                    required={rulesGroup[key].isMandatory}
                    placeholder={key}
                    items={rules[key]}
                    value={faults[key] || []}
                    onDeselect={(_value) => {
                      setFaults({
                                    ...faults,
                                    [key]: (faults[key] || []).filter(
                                            (faultCode) => faultCode !== _value,
                                    ),
                                });
                    }}
                    onSelect={(_value) => {
                        setFaults({
                                      ...faults,
                                      [key]: [...(faults[key] || []), _value],
                                  });
                    }}
                  />
                    <Space h={6} />
                </>
              ))}
                <Space h={6} />
                <Input
                        mode="textArea"
                        placeholder={t("DESCRIPTION")}
                        rows={6}
                        value={customerDefectsObs}
                        onChange={(e) => {
                            setCustomerDefectsObs(e.target.value);
                        }}
                />
                <Space h={32} />
            </div>
          )}
        </div>
      </div>
        <NextPreviousFooter
                onPreviousClick={() => {
                    setActiveStep(2);
                    onBack();
                }}
                nextLoading={loadingCreate}
                nextEnabled={Object.values(rulesGroup).reduce(
                        (result, {isMandatory, typeDesc}) => {
                            if (isMandatory) {
                                return result && (faults[typeDesc] || 0).length > 0;
                            }
                            return result;
                        },
                        true,
                )}
                previousEnabled={true}
                onNextClick={() => {
                    const _data = {
                        productCode: product.isUnknownRef ? null : product.productCode,
                        productDesc: product.isUnknownRef ? product.productDesc + " (" + product.productRef + ")" : null,
                        interventionTypeCode: product.intervention.code,
                        faultCodes: reduce(
                                faults,
                                (result, value, key) => [
                                    ...result,
                                    ...value.map((_value) => ({
                                        faultCode: _value,
                                        faultType: rulesGroup[key].typeCode,
                                    })),
                                ],
                                [],
                        ),
                        sn: product.sn,
                        sn2: product.sn2,
                        receiptLinekey: product.receiptLinekey,
                        purchaseDate: product.purchaseDate,
                        customerDefectsObs,
                    };
                    onCreate(_data);
                }}
        />
    </>
  );
};

ProductDetails.propTypes = {
    query: PropTypes.shape(
            {
                data: PropTypes.shape(
                        {
                            rules: PropTypes.shape(
                                    {
                                        data: PropTypes.shape(
                                                {
                                                    faultTypes: PropTypes.arrayOf(PropTypes.shape(
                                                            {
                                                                typeCode: PropTypes.string,
                                                                typeDesc: PropTypes.string,
                                                                isMandatory: PropTypes.bool,
                                                                faultGroupInfoList: PropTypes.arrayOf(
                                                                        {
                                                                            groupCode: PropTypes.string,
                                                                            groupDesc: PropTypes.string,
                                                                            faultCodeInfoList: PropTypes.arrayOf(
                                                                                    {
                                                                                        faultCode: PropTypes.string,
                                                                                        faultDesc: PropTypes.string,
                                                                                    }),
                                                                        }),
                                                            }),
                                                    ),
                                                }),
                                    }),
                        }),
                loading: PropTypes.bool.isRequired,
            }),
    setActiveStep: PropTypes.func.isRequired,
    step: PropTypes.shape({onBack: PropTypes.func.isRequired,}).isRequired,
    product: PropTypes.shape({
                                 productCode: PropTypes.string.isRequired,
                                 productRef: PropTypes.string,
                                 productDesc: PropTypes.string,
                                 isUnknownRef: PropTypes.bool,
                                 sn: PropTypes.string,
                                 sn2: PropTypes.string,
                                 purchaseDate: PropTypes.string,
                                 intervention: PropTypes.shape({
                                                                   code: PropTypes.string.isRequired,
                                                                   desc: PropTypes.string.isRequired,
                                                               }),
                                 receiptNo: PropTypes.string,
                                 receiptLinekey: PropTypes.string,
                             }),
    onCreate: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    warranty: PropTypes.string.isRequired,
};

ProductDetails.defaultProps = {};

export default StepStore(ProductDetails);
